// i18n composable — reactive language state + translation helpers
import { ref, computed } from 'vue'
import { translations, severityLabels, typeLabels, statusLabels, scopeLabels } from '@/i18n'
import type { SupportedLang } from '@/types'

const currentLang = ref<SupportedLang>(getInitialLanguage())

function getInitialLanguage(): SupportedLang {
  const stored = localStorage.getItem('statuspage-lang')
  if (stored === 'en' || stored === 'de') return stored

  const browserLang = navigator.language?.split('-')[0].toLowerCase()
  return browserLang === 'de' ? 'de' : 'en'
}

export function useI18n() {
  const lang = computed(() => currentLang.value)

  function t(key: string): string {
    const currentLangTranslation = translations[currentLang.value]
    if (currentLangTranslation && key in currentLangTranslation) {
      return currentLangTranslation[key]
    }
    const enTranslation = translations['en']
    if (enTranslation && key in enTranslation) {
      return enTranslation[key]
    }
    return key
  }

  function setLanguage(newLang: SupportedLang) {
    currentLang.value = newLang
    localStorage.setItem('statuspage-lang', newLang)
    document.documentElement.lang = newLang
  }

  function getEventTranslation(transObj: unknown, lang?: string): string {
    const l = lang || currentLang.value
    if (!transObj || typeof transObj !== 'object') return ''
    const obj = transObj as Record<string, string>
    if (obj[l] && obj[l].trim()) return obj[l]
    if (obj['de'] && obj['de'].trim()) return obj['de']
    for (const val of Object.values(obj)) {
      if (typeof val === 'string' && val.trim()) return val
    }
    return ''
  }

  function getTypeLabel(type: string): string {
    return typeLabels[currentLang.value]?.[type] || type
  }

  function getSeverityLabel(severity: string): string {
    return severityLabels[currentLang.value]?.[severity] || severity
  }

  function getStatusLabel(status: string): string {
    return statusLabels[currentLang.value]?.[status] || status
  }

  function getScopeLabel(scope: string): string {
    return scopeLabels[currentLang.value]?.[scope] || scope
  }

  return {
    lang,
    t,
    setLanguage,
    getEventTranslation,
    getTypeLabel,
    getSeverityLabel,
    getStatusLabel,
    getScopeLabel,
    severityLabels,
    typeLabels,
    statusLabels,
    scopeLabels,
  }
}
